import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as adminService from "../api/services/adminService";
import * as combinedTransactionService from "../api/services/combinedTransactionService";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { format, subDays } from "date-fns";
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, } from "recharts";
import { Input } from "@/shared/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import {
  ArrowLeft, Pencil, Users, Building2, TrendingUp, CheckCircle2, XCircle, Clock, Activity,
  Key, Shield, LogIn, Search, ChevronLeft, ChevronRight, Percent, Receipt, Eye,
} from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import DrillDownBreadcrumb from "@/shared/components/admin/DrillDownBreadcrumb";

const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30", label: "ACTIVE" },
  SUSPENDED: { icon: XCircle, color: "bg-red-500/20 text-red-400 border border-red-emerald-500/30", label: "SUSPENDED" },
  INACTIVE: { icon: Clock, color: "bg-amber-500/20 text-amber-400 border border-amber-500/30", label: "INACTIVE" },
};

const COLORS = ["#06b6d4", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

const DarkTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: "#0f172a", border: "1px solid #1e3a5f", borderRadius: 10, padding: "10px 14px" }}>
        <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 4 }}>{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color, fontSize: 13, fontWeight: 600 }}>
            {entry.name}: {entry.value?.toFixed(1)}K
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: "#0f172a", border: "1px solid #1e3a5f", borderRadius: 10, padding: "8px 12px" }}>
        <p style={{ color: "#06b6d4", fontSize: 13, fontWeight: 600 }}>
          {payload[0].name}: {payload[0].value}
        </p>
      </div>
    );
  }
  return null;
};

export default function AdminResellerDetail() {

  const urlParams = new URLSearchParams(window.location.search);
  const resellerId = urlParams.get("id");
  const [selectedTx, setSelectedTx] = useState(null);
  const [txFilters, setTxFilters] = useState({ search: "", status: "all", type: "all", merchant: "all" });
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: reseller } = useQuery({
    queryKey: ["reseller-detail", resellerId],
    queryFn: async () => adminService.getResellerById(resellerId),
    enabled: !!resellerId,
  });

  const { data: resellerMetrics = {} } = useQuery({
    queryKey: ["reseller-metrics", resellerId],
    queryFn: async () => adminService.getResellerDashboardLiveMetrics(resellerId),
    enabled: !!resellerId,
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["reseller-merchants-list", resellerId],
    queryFn: async () => adminService.getResellerMerchants(resellerId),
    enabled: !!resellerId,
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: async () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: async () => adminService.getAllResellers(),
  });

  // merchantIds respects existing merchant filter (no new UI added)
  const merchantIds = useMemo(() => {
    if (!merchants?.length) return [];
    if (txFilters.merchant && txFilters.merchant !== "all") return [txFilters.merchant];
    return merchants.map((m) => m.id);
  }, [merchants, txFilters.merchant]);

  // only map existing filters (status/type) — NO provider/date added
  const filtersMapper = useMemo(
    () => ({
      status: txFilters.status !== "all" ? txFilters.status : undefined,
      type: txFilters.type !== "all" ? txFilters.type.toUpperCase() : undefined,
    }),
    [txFilters.status, txFilters.type]
  );

  const {
    data: combinedQRCodeAndWitData,
    isLoading: isLoadingQRAndWithdrawals,
    refetch: refetchWithdrawalsAndQr,
    error: combinedError,
  } = useQuery({
    queryKey: ["merchant-withdrawals", page, pageSize, txFilters.search, filtersMapper, merchantIds],
    queryFn: () =>
      combinedTransactionService.getCombinedQRCodeAndWitData(
        page + 1,
        pageSize,
        txFilters.search,
        filtersMapper,
        merchantIds
      ),
    enabled: !!resellerId && merchantIds.length > 0,
  });

  const combinedDataWithdrawalsAndQRCodes = combinedQRCodeAndWitData?.data || [];

  const combinedData = useMemo(() => {
    return combinedDataWithdrawalsAndQRCodes
      .map((t) => ({
        ...t,
        transaction_id: t.transaction_id || t.payment_id || t.id,
        merchant_ref: t.merchant_ref || t.reference,
        type: t.type?.toUpperCase() || "UNKNOWN",
        status: (t.status || "").toLowerCase(),
        amount: t.amount,
        net_amount: t.net_amount || 0,
        total_fee: t.total_fee || 0,
        provider_display: t.bank_code || t.provider_code || "-",
        currency: t.currency,
        client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
        bank_name: t.bank_name,
        bank_code: t.bank_code,

        merchant:
          t.merchant ||
          (t.merchant_id ? merchants.find((m) => m.id === t.merchant_id) : null) || {
            name: t.merchant_name,
            id: t.merchant_id,
            code: t.merchant_code,
          },
        created_date: t.created_date || t.createdAt,
        processed_at: t.processed_at || t.updatedAt,
        completed_at: t.processed_at || t.updatedAt,
      }))
      .sort((a, b) => new Date(b.created_date || b.createdAt) - new Date(a.created_date || a.createdAt));
  }, [combinedDataWithdrawalsAndQRCodes, merchants]);

  const isLoading = isLoadingQRAndWithdrawals;
  const error = combinedError;

  const paginatedTransactions = combinedData;
  const totalPages = combinedQRCodeAndWitData?.totalPages || 0;
  const totalTransactions = combinedQRCodeAndWitData?.total || 0;

  const status = statusConfig[reseller?.status] || statusConfig.INACTIVE;
  const StatusIcon = status.icon;
  const parentEngine = engines?.[0];

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);

    const dayTxs = combinedData.filter((t) => {
      const d = new Date(t.created_date || t.createdAt);
      return d.toDateString() === date.toDateString() && (t.status || "").toLowerCase() === "success";
    });

    return {
      date: format(date, "MMM d"),
      deposits: dayTxs
        .filter((t) => t.type === "DEPOSIT")
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0) / 1000,
      withdrawals: dayTxs
        .filter((t) => t.type === "WITHDRAWAL")
        .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0) / 1000,
    };
  });

  const providerData = (reseller?.allowed_providers || ["SCB", "KBANK", "BBL"]).map((code) => ({
    name: code,
    value: Math.floor(Math.random() * 100) + 20,
  }));

  const loginLogs = [
    { date: "2024-01-15 14:32:00", ip: "103.45.67.89", status: "success" },
    { date: "2024-01-15 09:15:00", ip: "103.45.67.89", status: "success" },
    { date: "2024-01-14 18:45:00", ip: "203.112.45.67", status: "failed" },
    { date: "2024-01-14 11:20:00", ip: "103.45.67.89", status: "success" },
  ];

  const apiLogs = [
    { date: "2024-01-15 14:35:00", endpoint: "/v1/transactions", method: "POST", status: 200 },
    { date: "2024-01-15 14:34:00", endpoint: "/v1/merchants", method: "GET", status: 200 },
    { date: "2024-01-15 14:30:00", endpoint: "/v1/transactions", method: "POST", status: 201 },
    { date: "2024-01-15 14:28:00", endpoint: "/v1/balance", method: "GET", status: 200 },
  ];

  const statCards = [
    { title: "Today's Volume", value: `฿${Math.round(resellerMetrics?.todayVolume) || 0}`, icon: TrendingUp },
    { title: "Total Volume", value: `฿${Math.round(resellerMetrics?.totalVolume) || 0}`, icon: Activity },
    { title: "Success Rate", value: `${(resellerMetrics?.successRate ?? 0).toFixed(2) || 0}%`, icon: Percent },
    { title: "Merchants", value: `${resellerMetrics?.merchants || 0}`, icon: Building2 },
    { title: "Transactions", value: `${resellerMetrics?.totalTransactions || 0}`, icon: Activity },
  ];

  return (
    <div style={{ backgroundColor: "#060f1e", minHeight: "100vh", padding: "24px" }}>
      <style>{`
        .rs-dark-card { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 16px; overflow: hidden; }
        .rs-card-header { padding: 16px 20px; border-bottom: 1px solid #1e3a5f; }
        .rs-card-title { font-size: 15px; font-weight: 600; color: #e2e8f0; margin: 0; display: flex; align-items: center; gap: 8px; }
        .rs-card-content { padding: 20px; }
        .rs-input { background: #0a1628 !important; border: 1px solid #1e3a5f !important; color: #e2e8f0 !important; border-radius: 10px !important; }
        .rs-input::placeholder { color: #475569 !important; }
        .rs-input:focus { border-color: #06b6d4 !important; box-shadow: 0 0 0 2px rgba(6,182,212,0.1) !important; }
        .rs-select-trigger { background: #0a1628 !important; border: 1px solid #1e3a5f !important; color: #e2e8f0 !important; border-radius: 10px !important; }
        .rs-filter-bar { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 14px; padding: 16px; display: flex; flex-wrap: wrap; gap: 12px; }
        .rs-icon-btn { background: #0a1628; border: 1px solid #1e3a5f; border-radius: 8px; padding: 8px; color: #64748b; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center; }
        .rs-icon-btn:hover { border-color: #06b6d4; color: #06b6d4; }
        .rs-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 500; font-family: monospace; background: rgba(6,182,212,0.1); border: 1px solid rgba(6,182,212,0.3); color: #06b6d4; }
        .rs-pagination-btn { background: #0d1f35; border: 1px solid #1e3a5f; border-radius: 8px; padding: 6px 10px; color: #94a3b8; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; }
        .rs-pagination-btn:hover:not(:disabled) { border-color: #06b6d4; color: #06b6d4; }
        .rs-pagination-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .rs-log-row { display: flex; align-items: center; justify-content: space-between; padding: 12px; background: #0a1628; border: 1px solid #1e3a5f; border-radius: 10px; margin-bottom: 8px; }
        .rs-log-row:last-child { margin-bottom: 0; }
        .space-y-6 > * + * { margin-top: 24px; }
        .space-y-4 > * + * { margin-top: 16px; }
      `}</style>

      <div className="space-y-6">
        {/* Header */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <Link to={createPageUrl("AdminResellers")}>
              <button className="rs-icon-btn"
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#06b6d4"; e.currentTarget.style.color = "#06b6d4"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e3a5f"; e.currentTarget.style.color = "#64748b"; }}
              >
                <ArrowLeft style={{ width: 20, height: 20 }} />
              </button>
            </Link>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 24px rgba(139,92,246,0.3)" }}>
                <Users style={{ width: 32, height: 32, color: "#fff" }} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <h1 style={{ fontSize: 24, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>{reseller?.name}</h1>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500 }} className={status.color}>
                    <StatusIcon style={{ width: 12, height: 12 }} />
                    {status.label}
                  </span>
                </div>
                <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: 14 }}>{reseller?.code} • {reseller?.email}</p>
              </div>
            </div>
          </div>
          <Link to={createPageUrl("AdminResellerForm") + `?id=${resellerId}`}>
            <button
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                backgroundColor: "#06b6d4", color: "#fff",
                border: "none", borderRadius: 10, padding: "10px 18px",
                fontWeight: 600, fontSize: 14, cursor: "pointer",
                boxShadow: "0 0 16px rgba(6,182,212,0.4)", transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#0891b2"; e.currentTarget.style.boxShadow = "0 0 24px rgba(6,182,212,0.6)"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#06b6d4"; e.currentTarget.style.boxShadow = "0 0 16px rgba(6,182,212,0.4)"; }}
            >
              <Pencil style={{ width: 16, height: 16 }} />
              Edit Reseller
            </button>
          </Link>
        </div>

        {/* Drill Down Breadcrumb */}
        <DrillDownBreadcrumb engine={parentEngine} reseller={reseller} />

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
          {statCards.map((stat, i) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              style={{ background: "#0d1f35", border: "1px solid #1e3a5f", borderRadius: 16, padding: 16, transition: "all 0.2s", cursor: "default" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#06b6d4"; e.currentTarget.style.boxShadow = "0 0 20px rgba(6,182,212,0.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e3a5f"; e.currentTarget.style.boxShadow = "none"; }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <stat.icon style={{ width: 18, height: 18, color: "#06b6d4" }} />
                </div>
                <div>
                  <p style={{ fontSize: 11, color: "#64748b", margin: 0 }}>{stat.title}</p>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", margin: 0 }}>{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList style={{ background: "#0d1f35", border: "1px solid #1e3a5f", borderRadius: 12, padding: 4 }}>
            {[
              { value: "analytics", icon: Activity, label: "Analytics" },
              { value: "transactions", icon: Receipt, label: "All Transactions" },
              { value: "merchants", icon: Building2, label: "Merchants" },
              { value: "logs", icon: Shield, label: "Logs" },
            ].map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", fontSize: 13, fontWeight: 500, borderRadius: 8, color: "#64748b", transition: "all 0.2s" }}
                className="data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-400 data-[state=active]:border data-[state=active]:border-cyan-500/30 hover:text-cyan-400"
              >
                <tab.icon style={{ width: 15, height: 15 }} />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ gridColumn: "span 2" }}>
                <div className="rs-dark-card">
                  <div className="rs-card-header"><p className="rs-card-title">Volume Trend (7 Days)</p></div>
                  <div className="rs-card-content">
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id="rsDepositGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="rsWithdrawGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                          <XAxis dataKey="date" stroke="#475569" fontSize={11} tick={{ fill: "#64748b" }} />
                          <YAxis stroke="#475569" fontSize={11} tick={{ fill: "#64748b" }} tickFormatter={(v) => `${v}K`} />
                          <Tooltip content={<DarkTooltip />} />
                          <Area type="monotone" dataKey="deposits" name="Deposits" stroke="#06b6d4" strokeWidth={2} fill="url(#rsDepositGrad)" />
                          <Area type="monotone" dataKey="withdrawals" name="Withdrawals" stroke="#10b981" strokeWidth={2} fill="url(#rsWithdrawGrad)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <div className="rs-dark-card" style={{ height: "100%" }}>
                  <div className="rs-card-header"><p className="rs-card-title">Provider Distribution</p></div>
                  <div className="rs-card-content">
                    <div style={{ height: 200 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={providerData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={5} dataKey="value">
                            {providerData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip content={<PieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, justifyContent: "center" }}>
                      {providerData.map((entry, index) => (
                        <div key={entry.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: COLORS[index % COLORS.length] }} />
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>{entry.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              <div style={{ gridColumn: "span 3" }}>
                <div className="rs-dark-card">
                  <div className="rs-card-header"><p className="rs-card-title">Recent 20 Transactions</p></div>
                  <div className="rs-card-content">
                    <AdminTransactionTable transactions={combinedData} onView={setSelectedTx} showReseller={false} compact merchants={merchants} resellers={resellers} />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* All Transactions Tab */}
          <TabsContent value="transactions">
            <div className="space-y-4">
              <div className="rs-filter-bar">
                <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                  <Search
                    style={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 16,
                      height: 16,
                      color: "#475569",
                    }}
                  />
                  <Input
                    placeholder="Search by ID or reference..."
                    value={txFilters.search}
                    onChange={(e) => {
                      setTxFilters({ ...txFilters, search: e.target.value });
                      setPage(0);
                    }}
                    className="rs-input"
                    style={{ paddingLeft: 38 }}
                  />
                </div>

                <Select
                  value={txFilters.type}
                  onValueChange={(v) => {
                    setTxFilters({ ...txFilters, type: v });
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="rs-select-trigger" style={{ width: 140 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: "#0d1f35", border: "1px solid #1e3a5f" }}>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="DEPOSIT">Deposits</SelectItem>
                    <SelectItem value="WITHDRAWAL">Withdrawals</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={txFilters.status}
                  onValueChange={(v) => {
                    setTxFilters({ ...txFilters, status: v });
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="rs-select-trigger" style={{ width: 140 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: "#0d1f35", border: "1px solid #1e3a5f" }}>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="SUCCESS">Success</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="PROCESSING">Processing</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <AdminTransactionTable
                transactions={paginatedTransactions}
                isLoading={isLoading}
                onView={setSelectedTx}
                showReseller={false}
                merchants={merchants}
                resellers={resellers}
              />

              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: 13, color: "#64748b" }}>
                    Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalTransactions)} of {totalTransactions}
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button className="rs-pagination-btn" onClick={() => setPage(page - 1)} disabled={page === 0}>
                      <ChevronLeft style={{ width: 16, height: 16 }} />
                    </button>
                    <span style={{ fontSize: 13, color: "#94a3b8" }}>
                      Page {page + 1} of {totalPages}
                    </span>
                    <button className="rs-pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                      <ChevronRight style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Merchants Tab */}
          <TabsContent value="merchants">
            <div className="rs-dark-card">
              <div className="rs-card-header"><p className="rs-card-title">Merchants Under This Reseller ({merchants.length})</p></div>
              <div className="rs-card-content">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderBottom: "1px solid #1e3a5f" }}>
                      {["Merchant", "Code", "Status", "Deposit Fee", "Withdrawal Fee", "Created", ""].map((h, i) => (
                        <TableHead key={i} style={{ color: "#475569", fontSize: 12, fontWeight: 500, background: "#0a1628" }}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {merchants.map(merchant => (
                      <TableRow key={merchant.id} style={{ borderBottom: "1px solid #1a2f4a", cursor: "pointer", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(6,182,212,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <TableCell style={{ color: "#e2e8f0", fontSize: 13 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Building2 style={{ width: 16, height: 16, color: "#06b6d4" }} />
                            </div>
                            <div>
                              <p style={{ fontWeight: 500, margin: 0, color: "#e2e8f0" }}>{merchant.name}</p>
                              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>{merchant.contact_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><span className="rs-badge">{merchant.code}</span></TableCell>
                        <TableCell><span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 500, display: "inline-block" }} className={statusConfig[merchant.status]?.color || ""}>{merchant.status}</span></TableCell>
                        <TableCell style={{ color: "#94a3b8", fontSize: 13 }}>{merchant?.depositFee != null ? `${merchant.depositFee}%` : "N/A"}</TableCell>
                        <TableCell style={{ color: "#94a3b8", fontSize: 13 }}>{merchant?.withdrawalFee != null ? `${merchant.withdrawalFee}%` : "N/A"}</TableCell>
                        <TableCell style={{ color: "#64748b", fontSize: 12 }}>{merchant.createdAt ? format(new Date(merchant.createdAt), "MMM d, yyyy") : "N/A"}</TableCell>
                        <TableCell>
                          <Link to={createPageUrl("AdminMerchantDetail") + `?id=${merchant.id}`}>
                            <button className="rs-icon-btn"><Eye style={{ width: 15, height: 15 }} /></button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {/* Login Logs */}
              <div className="rs-dark-card">
                <div className="rs-card-header">
                  <p className="rs-card-title"><LogIn style={{ width: 18, height: 18, color: "#06b6d4" }} />Login Logs</p>
                </div>
                <div className="rs-card-content">
                  {loginLogs.map((log, i) => (
                    <div key={i} className="rs-log-row">
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 500, color: "#e2e8f0", margin: "0 0 2px" }}>{log.date}</p>
                        <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>IP: {log.ip}</p>
                      </div>
                      <span style={{
                        padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                        background: log.status === "success" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)",
                        border: `1px solid ${log.status === "success" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
                        color: log.status === "success" ? "#10b981" : "#ef4444",
                      }}>
                        {log.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* API Usage Logs */}
              <div className="rs-dark-card">
                <div className="rs-card-header">
                  <p className="rs-card-title"><Key style={{ width: 18, height: 18, color: "#06b6d4" }} />API Usage Logs</p>
                </div>
                <div className="rs-card-content">
                  {apiLogs.map((log, i) => (
                    <div key={i} className="rs-log-row">
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 500, color: "#e2e8f0", margin: "0 0 2px", fontFamily: "monospace" }}>
                          <span style={{ color: log.method === "GET" ? "#06b6d4" : "#10b981", marginRight: 6 }}>{log.method}</span>
                          {log.endpoint}
                        </p>
                        <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>{log.date}</p>
                      </div>
                      <span style={{
                        padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: "monospace",
                        background: log.status < 400 ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)",
                        border: `1px solid ${log.status < 400 ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
                        color: log.status < 400 ? "#10b981" : "#ef4444",
                      }}>
                        {log.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} engine={parentEngine} reseller={reseller} />
    </div>
  );
}